import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, RefreshCw, Plus } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const Page = styled.div`min-height:100vh;background:#0a0a0f;color:#e2e8f0;font-family:'Inter','Pretendard',sans-serif;padding:20px;`;
const TopBar = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;&:hover{background:#1e2030;color:#e2e8f0}`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;background:linear-gradient(135deg,#8b5cf6,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;
const NavTabs = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const NavTab = styled.button`
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;
  &:hover{border-color:#8b5cf6;color:#8b5cf6}
`;

const Toolbar = styled.div`display:flex;gap:10px;align-items:center;margin-bottom:20px;flex-wrap:wrap;`;
const Select = styled.select`padding:8px 12px;background:#111827;border:1px solid #374151;border-radius:8px;color:#e2e8f0;font-size:13px;&:focus{outline:none;border-color:#8b5cf6}`;
const Btn = styled.button`
  display:flex;align-items:center;gap:6px;padding:10px 18px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;
  background:${p => p.primary ? 'rgba(139,92,246,0.2)' : '#1e2030'};
  color:${p => p.primary ? '#8b5cf6' : '#94a3b8'};
  border:1px solid ${p => p.primary ? '#8b5cf6' : '#374151'};
  &:hover{opacity:.85}&:disabled{opacity:.4;cursor:not-allowed}
`;

const Pipeline = styled.div`
  display:flex;gap:0;margin-bottom:24px;animation:${fadeIn} .4s ease;
`;
const PipeStep = styled.div`
  flex:1;background:#111827;padding:16px;text-align:center;border:1px solid #1e2030;
  &:first-child{border-radius:12px 0 0 12px}
  &:last-child{border-radius:0 12px 12px 0}
`;
const PipeNum = styled.div`font-size:28px;font-weight:800;color:${p => p.color || '#e2e8f0'};`;
const PipeLabel = styled.div`font-size:11px;color:#64748b;margin-top:4px;`;
const PipeArrow = styled.div`display:flex;align-items:center;color:#374151;font-size:18px;`;

const Table = styled.div`background:#111827;border:1px solid #1e2030;border-radius:14px;overflow-x:auto;animation:${fadeIn} .5s ease;`;
const TH = styled.div`
  display:grid;grid-template-columns:60px 1fr 80px 60px 80px 60px 80px 60px;gap:8px;
  padding:12px 16px;background:#0d1117;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;min-width:700px;
`;
const TR = styled.div`
  display:grid;grid-template-columns:60px 1fr 80px 60px 80px 60px 80px 60px;gap:8px;
  padding:12px 16px;border-bottom:1px solid #1e2030;font-size:12px;min-width:700px;transition:background .15s;
  &:hover{background:#1a1f2e}&:last-child{border-bottom:none}
`;
const VcpBadge = styled.span`
  font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;
  background:${p => p.status === 'BREAKOUT' ? 'rgba(16,185,129,0.15)' : p.status === 'READY' ? 'rgba(245,158,11,0.15)' : p.status === 'FORMING' ? 'rgba(6,182,212,0.15)' : 'rgba(100,116,139,0.15)'};
  color:${p => p.status === 'BREAKOUT' ? '#10b981' : p.status === 'READY' ? '#f59e0b' : p.status === 'FORMING' ? '#06b6d4' : '#64748b'};
`;
const ProgressBar = styled.div`
  background:#1e2030;border-radius:8px;padding:12px 16px;margin-bottom:20px;animation:${fadeIn} .3s ease;
`;
const ProgressText = styled.div`font-size:12px;color:#94a3b8;margin-bottom:6px;`;
const ProgressTrack = styled.div`height:6px;background:#0d1117;border-radius:3px;overflow:hidden;`;
const ProgressFill = styled.div`height:100%;background:linear-gradient(90deg,#8b5cf6,#f59e0b);border-radius:3px;transition:width .3s;`;

function MinerviniScreenerPage() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [market, setMarket] = useState('ALL');
  const [mode, setMode] = useState('HIGH');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastDate, setLastDate] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const latest = await apiService.getMinerviniScreener();
        if (latest?.results) { setResults(latest.results); setPipeline(latest.pipeline || {}); setLastDate(latest.date || ''); }
        const hist = await apiService.getMinerviniScreenerHistory();
        setHistory(hist || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const handleRun = async () => {
    setRunning(true); setProgress({ current: 0, total: 0 }); setResults([]);
    try {
      await apiService.runMinerviniScreener({ market, mode }, (data) => {
        if (data.type === 'start') setProgress({ current: 0, total: data.total, symbol: '' });
        else if (data.type === 'progress') setProgress({ current: data.current, total: data.total, symbol: data.symbol, name: data.name });
        else if (data.type === 'hit') setResults(prev => [...prev, data]);
        else if (data.done) { setPipeline(data.pipeline || {}); setResults(data.results || []); setLastDate(data.date || ''); }
      });
    } catch (e) { alert('스크리닝 오류: ' + e.message); }
    finally { setRunning(false); setProgress(null); }
  };

  const addToWatchlist = async (item) => {
    try {
      const config = await apiService.getMinerviniConfig();
      const exists = (config.watchlist || []).find(w => w.symbol === item.symbol);
      if (exists) { alert(`${item.name}은 이미 감시목록에 있습니다.`); return; }
      const updated = [...(config.watchlist || []), { symbol: item.symbol, name: item.name, market: item.market || 'KOSPI' }];
      await apiService.updateMinerviniConfig({ watchlist: updated });
      alert(`✅ ${item.name}을 감시목록에 추가했습니다.`);
    } catch (e) { alert('오류: ' + e.message); }
  };

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/minervini')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>🔍 VCP 종목 스크리너</PageTitle>
          <PageSub>3단계 파이프라인: Trend Template → VCP → VDU</PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/minervini')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/journal')}>거래일지</NavTab>
          <NavTab active onClick={() => navigate('/labs/minervini/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <Toolbar>
        <Select value={market} onChange={e => setMarket(e.target.value)}>
          <option value="ALL">전체 시장</option>
          <option value="KOSPI">KOSPI</option>
          <option value="KOSDAQ">KOSDAQ</option>
        </Select>
        <Select value={mode} onChange={e => setMode(e.target.value)} title="조건 강도">
          <option value="HIGH">조건: 상 (기본)</option>
          <option value="MEDIUM">조건: 중 (완화됨)</option>
          <option value="LOW">조건: 하 (매우 완화됨)</option>
        </Select>
        {lastDate && <span style={{ fontSize: 12, color: '#64748b' }}>마지막: {lastDate}</span>}
        <Btn primary onClick={handleRun} disabled={running}>
          <RefreshCw size={14} />
          {running ? '스크리닝 중...' : '🔄 스크리닝 실행'}
        </Btn>
      </Toolbar>

      {progress && (
        <ProgressBar>
          <ProgressText>
            {progress.current}/{progress.total} 스캔 중...
            {progress.name && <span style={{ marginLeft: 8, color: '#e2e8f0' }}>{progress.name} ({progress.symbol})</span>}
          </ProgressText>
          <ProgressTrack>
            <ProgressFill style={{ width: progress.total > 0 ? `${(progress.current / progress.total) * 100}%` : '0%' }} />
          </ProgressTrack>
        </ProgressBar>
      )}

      {(pipeline.totalScanned > 0) && (
        <Pipeline>
          <PipeStep><PipeNum color="#94a3b8">{pipeline.totalScanned || 0}</PipeNum><PipeLabel>전체 스캔</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#8b5cf6">{pipeline.templatePassed || 0}</PipeNum><PipeLabel>TT 통과</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#06b6d4">{pipeline.vcpDetected || 0}</PipeNum><PipeLabel>VCP 감지</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#f59e0b">{pipeline.vduConfirmed || 0}</PipeNum><PipeLabel>VDU 확인</PipeLabel></PipeStep>
          <PipeArrow>→</PipeArrow>
          <PipeStep><PipeNum color="#10b981">{pipeline.breakoutReady || 0}</PipeNum><PipeLabel>매수 대기</PipeLabel></PipeStep>
        </Pipeline>
      )}

      {results.length > 0 ? (
        <Table>
          <TH><span>추가</span><span>종목</span><span>TT점수</span><span>RS</span><span>VCP</span><span>VDU</span><span>피벗</span><span>현재가</span></TH>
          {results.map((r, i) => (
            <TR key={r.symbol || i}>
              <div>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b5cf6', padding: 2 }}
                  onClick={() => addToWatchlist(r)} title="감시목록 추가"><Plus size={16} /></button>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                <div style={{ fontSize: 10, color: '#475569' }}>{r.symbol} · {r.market}</div>
              </div>
              <div>
                <span style={{ fontWeight: 700, color: r.templateScore >= 90 ? '#10b981' : r.templateScore >= 75 ? '#f59e0b' : '#94a3b8' }}>
                  {r.templateScore}/100
                </span>
              </div>
              <span style={{ fontWeight: 600, color: r.relativeStrength >= 70 ? '#f59e0b' : '#64748b' }}>{r.relativeStrength}</span>
              <VcpBadge status={r.vcpStatus}>
                {r.vcpStatus === 'NONE' ? '—' : `${r.vcpTCount || '?'}T ${r.vcpStatus}`}
              </VcpBadge>
              <span style={{ color: r.vduStatus ? '#10b981' : '#64748b' }}>
                {r.vduStatus ? '✅' : r.vduRatio ? `${r.vduRatio}x` : '—'}
              </span>
              <span style={{ fontWeight: 600 }}>{r.pivotPoint ? fmt(r.pivotPoint) : '—'}</span>
              <span>{fmt(r.currentPrice)}</span>
            </TR>
          ))}
        </Table>
      ) : !running && (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569', background: '#111827', borderRadius: 14 }}>
          스크리닝을 실행하면 VCP 종목이 여기에 표시됩니다.
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>📋 스크리닝 이력</h3>
          {history.slice(0, 10).map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 16, padding: '8px 0', borderBottom: '1px solid #1e2030', fontSize: 12, color: '#94a3b8' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{h.date}</span>
              <span>TT: {h.pipeline?.templatePassed || 0}</span>
              <span>VCP: {h.pipeline?.vcpDetected || 0}</span>
              <span>VDU: {h.pipeline?.vduConfirmed || 0}</span>
              <span>결과: {h.resultsCount || 0}종목</span>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

export default MinerviniScreenerPage;
